/**
 * Hides products from storefront Searchspring surfaces (search results,
 * autocomplete drawer cards, and recommendations) while they stay ACTIVE in
 * Shopify - e.g. "Shipping Protection by Route" must remain active so AP21 can
 * auto-map it to orders, but should never be shopper-facing.
 *
 * There are two complementary mechanisms:
 *
 *  1. Tag-driven (merchant self-serve, ongoing). Searchspring indexes Shopify
 *     tags and returns them on each result, so any product tagged
 *     `hide-from-search` in Shopify is excluded here. Merchants can hide/show
 *     products themselves by adding/removing the tag - no code change. Note a
 *     newly-added tag only takes effect once Searchspring re-indexes (their
 *     schedule), so it is the right ongoing lever but not instant.
 *
 *  2. Identifier list (immediate, guaranteed). For products that must be hidden
 *     right now, before any re-index. "Shipping Protection by Route" lives here
 *     so it disappears the moment this ships, independent of its tag's index
 *     status. Match is on any stable id the result shapes expose (Shopify
 *     product id / SKU / handle); the Searchspring internal `id` hash is never
 *     used as it is not a stable Shopify id.
 *
 * To hide a product going forward, prefer tagging it `hide-from-search` in
 * Shopify. Add an entry below only when it must take effect before a re-index.
 */
const EXCLUDED_TAGS = new Set<string>(["hide-from-search"])
const EXCLUDED_HANDLES = new Set<string>(["routeins"])
const EXCLUDED_SKUS = new Set<string>(["ROUTEINS10"])
const EXCLUDED_PRODUCT_IDS = new Set<string>(["7509448130694"])

/** Normalise a bare handle or a `/products/<handle>` URL down to the handle. */
function normaliseHandle(value?: unknown): string {
  if (typeof value !== "string") return ""
  return value
    .replace(/^.*\/products\//, "")
    .split(/[?#]/)[0]
    .toLowerCase()
}

/** Coerce a Searchspring `tags` value (array, or comma string) to a list. */
function toTagList(value?: unknown): string[] {
  if (Array.isArray(value))
    return value.filter((t): t is string => typeof t === "string")
  if (typeof value === "string") return value.split(",")
  return []
}

/**
 * True when a Searchspring result/recommendation item is excluded - either by
 * carrying an excluded tag or by matching an excluded identifier. Non-product
 * entries (e.g. inline content-banner strings) return false so they pass
 * through untouched.
 */
export function isExcludedProduct(item: unknown): boolean {
  if (!item || typeof item !== "object") return false
  const result = item as Record<string, unknown>
  const attributes = (result.attributes ?? {}) as Record<string, unknown>

  const tags = toTagList(result.tags ?? attributes.tags)
  if (tags.some((tag) => EXCLUDED_TAGS.has(tag.trim().toLowerCase())))
    return true

  const uid = String(result.uid ?? attributes.uid ?? "")
  if (uid && EXCLUDED_PRODUCT_IDS.has(uid)) return true

  const sku = String(result.sku ?? attributes.sku ?? "")
  if (sku && EXCLUDED_SKUS.has(sku)) return true

  const handle = normaliseHandle(
    attributes.handle ?? result.handle ?? result.url
  )
  if (handle && EXCLUDED_HANDLES.has(handle)) return true

  return false
}

/** Remove excluded products from a Searchspring results/recommendations array. */
export function excludeProducts<T>(items: T[]): T[] {
  return items.filter((item) => !isExcludedProduct(item))
}
